/**
 * Analytics API client — events, distribution metrics, and dashboard data.
 *
 * Mirrors the gateway's /api/analytics routes:
 *  - brand tier:     overview (any member) + events feed (admin/owner)
 *  - universal tier: all-brands overview + feed (superadmin only)
 *  - user tier:      profile summary + personal events (self or superadmin)
 */

import apiClient from './apiClient';

export type AnalyticsRange = 'today' | '7d' | '14d' | '30d' | 'all';
export type EventCategory = 'all' | 'publishing' | 'tending' | 'edits' | 'feedback' | 'agent' | 'analytics' | 'config' | 'sessions' | 'system';

export type AnalyticsEventType =
  | 'user.login'
  | 'user.registered'
  | 'user.added_to_brand'
  | 'user.role_changed'
  | 'user.removed_from_brand'
  | 'content.imported'
  | 'tend.created'
  | 'tend.edited'
  | 'item.feedback'
  | 'agent.run'
  | 'agent.burst'
  | 'agent.request'
  | 'analytics.insights_run'
  | 'video.generated'
  | 'post.published'
  | 'post.queued'
  | 'post.reordered'
  | 'post.held'
  | 'take.requested'
  | 'post.failed'
  | 'post.scheduled'
  | 'post.deleted'
  | 'social.linked'
  | 'social.unlinked'
  | 'social.relink_required'
  | 'config.guidelines_updated'
  | 'config.strategy_updated'
  | 'config.strategy_reverted'
  | 'config.tactic_created'
  | 'config.tactic_updated'
  | 'config.tactic_deleted'
  | 'config.tactic_toggled'
  | 'config.prompt_changed'
  | 'config.template_changed'
  | 'config.font_changed'
  | 'feed.added'
  | 'feed.toggled'
  | 'feed.removed'
  | 'brand.created'
  | 'brand.archived'
  | 'brand.restored'
  | 'brand.deleted'
  | 'brand.settings_changed'
  | 'brand.logo_updated';

/** Category → event types; must stay in sync with EVENT_CATEGORIES on the backend. */
export const EVENT_CATEGORY_TYPES: Record<Exclude<EventCategory, 'all'>, AnalyticsEventType[]> = {
  publishing: ['post.published', 'post.failed', 'post.scheduled', 'post.deleted', 'post.held', 'post.queued', 'post.reordered'],
  tending: ['tend.created', 'content.imported', 'video.generated', 'take.requested'],
  // tend.edited fires on every save — its own chip keeps the flood contained
  edits: ['tend.edited'],
  feedback: ['item.feedback'],
  // Everything an agent can originate through the TEND MCP server; the tab
  // then keeps only rows whose actor_type is 'agent' (the same types also
  // fire for people)
  agent: ['agent.run', 'agent.burst', 'agent.request', 'tend.created', 'tend.edited', 'item.feedback', 'content.imported'],
  // Editorial insights runs (the Analytics header's Run button)
  analytics: ['analytics.insights_run'],
  config: [
    'config.guidelines_updated', 'config.strategy_updated', 'config.strategy_reverted',
    'config.tactic_created', 'config.tactic_updated', 'config.tactic_deleted', 'config.tactic_toggled',
    'config.prompt_changed', 'config.template_changed', 'config.font_changed',
    'feed.added', 'feed.toggled', 'feed.removed',
    'brand.settings_changed', 'brand.logo_updated',
  ],
  sessions: ['user.login'],
  system: [
    'social.linked', 'social.unlinked', 'social.relink_required',
    'user.registered', 'user.added_to_brand', 'user.role_changed', 'user.removed_from_brand',
    'brand.created', 'brand.archived', 'brand.restored', 'brand.deleted',
  ],
};

export interface StatWithDelta {
  value: number | null;
  /** Relative % change vs prior window (percentage POINTS for success_rate); null = no comparison */
  delta_pct: number | null;
}

export interface FunnelStats {
  articles: number;
  tends: number;
  posts_published: number;
  clicks: number;
}

/** Combined followers across channels. `delta_abs` is growth INSIDE the
 * window (count now vs the count at the window start; all-time = since
 * we first measured), the number the card shows; `delta_pct` is the same
 * growth as a percentage of the starting count. Both null with no
 * baseline yet. */
export interface FollowersStat extends StatWithDelta {
  delta_abs: number | null;
}

export interface OverviewCards {
  posts_published: StatWithDelta;
  posts_failed: StatWithDelta;
  success_rate: StatWithDelta;
  engagement: StatWithDelta;
  impressions: StatWithDelta;
  clicks: StatWithDelta;
  /** Still computed (user-stats popover reads it); no longer a summary card. */
  active_users: StatWithDelta;
  followers: FollowersStat;
  /** Items the queue is holding for an edit right now (live, not windowed;
   * never part of the rate — nothing was sent). */
  held?: StatWithDelta;
}

/** One queue ticket the SYSTEM holds (today: post text over the channel's
 * character ceiling). Clears itself when the item is edited under and publishes. */
export interface HeldItem {
  content_id: number;
  brand_id: number;
  title: string | null;
  reason: 'over_limit' | string;
  detail: string | null;
  held_since: string | null;
}

export interface ChannelStats {
  platform: string;
  connected: boolean;
  username: string | null;
  followers: number | null;
  /** Growth over the selected period (all-time: since we started measuring); null = no baseline yet */
  followers_delta: number | null;
  /** Date of the latest follower snapshot (brand scope only) */
  followers_as_of?: string | null;
  /** Where the latest count came from: staff entry or the Ayrshare daily pull */
  followers_source?: 'manual' | 'ayrshare' | null;
  posts: number;
  success_rate: number | null;
  /** Failed attempt records in the window, retried or not — the failures
   * expansion has rows exactly when this is > 0 (the bar itself reflects
   * each leg's latest outcome, so it can read 100% with attempts behind it). */
  failed_attempts: number;
  /** Live SYSTEM holds on this channel (over the character ceiling) — a
   * warning, never an outcome: not in the rate, not a failed attempt. */
  held?: number;
  held_items?: HeldItem[];
  /** Snapshot metrics are null when the platform's API never reported the
   * metric for any leg ("NR" — e.g. Bluesky has no view counts at all),
   * distinct from a reported zero. */
  impressions: number | null;
  likes: number | null;
  /** Excludes our own auto 1st-comments (count in own_comments_excluded) */
  comments: number | null;
  shares: number | null;
  /** likes + comments + shares + saves + non-like reactions (aggregate) */
  engagement: number | null;
  /** Own auto 1st-comments excluded from `comments` in this window */
  own_comments_excluded: number;
  /** First-party short-link clicks, humans only — except where the network
   * reports its own (`clicks_source` 'network': Pinterest outbound clicks;
   * null there = never reported), or where its posts carry no link at all
   * (`clicks_source` 'none': TikTok, Snapchat, Instagram; always null = NR). */
  clicks: number | null;
  clicks_source?: 'link' | 'network' | 'none';
  /** Short-link fetches filtered as bots (crawlers, preview prefetches) */
  bot_clicks: number;
}

/** One failed publish attempt from /brands/{id}/failures. */
export type PublishFailureState = 'retrying' | 'paused' | 'recovered' | 'failed';

export interface PublishFailureRow {
  history_id: number;
  content_id: number | null;
  platforms: string[];
  error_message: string | null;
  created_at: string | null;
  title: string | null;
  // Amber model: whether a retry may still resolve this (retrying/paused),
  // a retry already healed it (recovered), or it's the final outcome (failed).
  state?: PublishFailureState;
  attempts?: number | null;
  max_attempts?: number | null;
  reason?: string | null;
  /** The dispatcher's failure kind (rejected = parked on the first attempt). */
  kind?: string | null;
  recovered_at?: string | null;
  recovered_via?: 'manual' | 'queue' | string | null;
  /** The network's numeric error code and its details string, when the
      reply carried them (item 5227: the alert had the code, the UI didn't). */
  code?: number | string | null;
  details?: string | null;
}

export interface BrandFailuresResponse {
  success: boolean;
  brand_id: number;
  failures: PublishFailureRow[];
}

/** Per-post lifetime metrics (latest snapshot totals) from /brands/{id}/posts.
 * With a snapshot on record, null = the platform never reported that metric
 * ("NR" — e.g. Bluesky impressions), distinct from a reported zero. */
export interface PostMetrics {
  impressions: number | null;
  likes: number | null;
  /** Excludes our own auto 1st-comment (see own_comments_excluded) */
  comments: number | null;
  shares: number | null;
  /** Platform-reported per-post clicks (exact, from metric snapshots) */
  clicks: number | null;
  /** First-party short-link clicks — different source, never sum with clicks */
  link_clicks: number;
  /** Which of the two the Clicks cell shows: 'network' for Pinterest
   * (outbound clicks, its pins carry no TEND link), 'none' where posts
   * carry no link at all (TikTok, Snapchat, Instagram: NR), 'link' elsewhere. */
  clicks_source?: 'link' | 'network' | 'none';
  engagement: number | null;
  video_views: number | null;
  saves: number | null;
  /** Our own auto 1st-comment excluded from `comments` (0 or 1) */
  own_comments_excluded: number;
  captured_at: string | null;
  /** false = no snapshot yet (fresh post, or a platform without analytics) */
  metrics_available: boolean;
}

export interface PublishedPostRow {
  publish_history_id: number;
  tend_content_id: number;
  ayrshare_post_id: string | null;
  /** TEND channel token (e.g. youtube_shorts) — NOT the Ayrshare name */
  platform: string;
  published_at: string | null;
  post_url: string | null;
  title: string | null;
  thumbnail_url: string | null;
  content_mode: string | null;
  metrics: PostMetrics;
}

export interface BrandPostsResponse {
  success: boolean;
  brand_id: number;
  posts: PublishedPostRow[];
  total: number;
}

export interface ConnectionHealth {
  platform: string;
  brand_id: number;
  brand_name: string | null; // populated on universal scope only
  is_connected: boolean;
  username: string | null;
  last_checked_at: string | null;
  status: 'ok' | 'warn' | 'error';
  detail: string;
}

export interface PulseBucket {
  bucket: string; // ISO hour (today), ISO date (7d/30d), or year-week (all)
  count: number;
}

export interface BrandOverviewResponse {
  range: AnalyticsRange;
  start: string | null;
  end: string;
  funnel: FunnelStats;
  cards: OverviewCards;
  channels: ChannelStats[];
  connection_health: ConnectionHealth[];
  pulse: PulseBucket[];
}

export interface BrandRollup {
  brand_id: number;
  name: string;
  slug: string;
  articles: number;
  tends: number;
  posts_published: number;
  posts_failed: number;
  success_rate: number | null;
  engagement: number;
  impressions: number;
  clicks: number;
  last_activity_at: string | null;
}

export interface UniversalOverviewResponse extends BrandOverviewResponse {
  brands: BrandRollup[];
}

export interface EventActor {
  id: string;
  name: string;
}

export interface EventBrand {
  id: number;
  name: string;
  slug: string;
}

export interface AnalyticsEvent {
  id: number;
  event_type: AnalyticsEventType;
  actor: EventActor | null;
  actor_type: 'user' | 'system' | 'webhook' | 'backfill' | 'agent';
  brand: EventBrand | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface EventsResponse {
  events: AnalyticsEvent[];
  has_more: boolean;
}

export interface EventsQuery {
  types?: AnalyticsEventType[];
  limit?: number;
  before_id?: number;
  after_id?: number;
}

export interface UserActivityDay {
  date: string;
  count: number;
}

export interface UserSummaryStats {
  logins: number;
  tends_created: number;
  posts_published: number;
  videos_generated: number;
  brands_touched: number;
}

export interface UserSession {
  created_at: string;
  client_ip: string | null;
  user_agent: string | null;
}

export interface InsightsResponse {
  insight: string | null;
  generated_at: string;
  /** OpenRouter model id that produced this insight (null for canned messages) */
  model: string | null;
  model_name: string | null;
  cached: boolean;
}

export interface LlmModel {
  id: string;
  name: string;
  provider: string;
}

export interface UserSummaryResponse {
  user: { id: string; name: string };
  range: AnalyticsRange;
  stats: UserSummaryStats;
  activity: UserActivityDay[]; // 14 entries, oldest first
  recent_items: AnalyticsEvent[];
  sessions: UserSession[];
}

/** Viewer's IANA timezone — the backend uses it for Today/7d/30d day
 * boundaries (server default: America/New_York). */
const BROWSER_TZ: string | undefined = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
})();

function eventParams(query: EventsQuery): Record<string, any> {
  const params: Record<string, any> = {};
  if (query.types?.length) params.types = query.types.join(',');
  if (query.limit) params.limit = query.limit;
  if (query.before_id) params.before_id = query.before_id;
  if (query.after_id) params.after_id = query.after_id;
  return params;
}

export const analyticsApi = {
  async getBrandOverview(brandId: number, range: AnalyticsRange): Promise<BrandOverviewResponse> {
    return apiClient.get<BrandOverviewResponse>(`analytics/brands/${brandId}/overview`, { range, tz: BROWSER_TZ });
  },

  async getBrandEvents(brandId: number, query: EventsQuery = {}): Promise<EventsResponse> {
    return apiClient.get<EventsResponse>(`analytics/brands/${brandId}/events`, eventParams(query));
  },

  /** Per-post lifetime metrics for the Published page list view; one row per
   * successful publish, newest first. Sorting/filtering is client-side. */
  async getBrandPosts(brandId: number, platform?: string): Promise<BrandPostsResponse> {
    const params: Record<string, any> = {};
    if (platform) params.platform = platform;
    return apiClient.get<BrandPostsResponse>(`analytics/brands/${brandId}/posts`, params);
  },

  /** Failed publish legs for the ChannelTable's Success expansion — same
   * window and per-leg accounting as the success bar it explains. */
  async getBrandPublishFailures(
    brandId: number,
    platform: string | undefined,
    range: AnalyticsRange,
  ): Promise<BrandFailuresResponse> {
    const params: Record<string, any> = { range, tz: BROWSER_TZ };
    if (platform) params.platform = platform;
    return apiClient.get<BrandFailuresResponse>(`analytics/brands/${brandId}/failures`, params);
  },

  async getBrandInsights(
    brandId: number,
    range: AnalyticsRange,
    opts?: { refresh?: boolean; model?: string },
  ): Promise<InsightsResponse> {
    const params: Record<string, any> = { range, tz: BROWSER_TZ };
    if (opts?.refresh) params.refresh = true;
    if (opts?.model) params.model = opts.model;
    return apiClient.get<InsightsResponse>(`analytics/brands/${brandId}/insights`, params);
  },

  /** OpenRouter catalog for the insights model dropdowns (superadmin only) */
  async getLlmModels(): Promise<{ models: LlmModel[] }> {
    return apiClient.get<{ models: LlmModel[] }>('analytics/llm-models');
  },

  async setChannelFollowers(
    brandId: number,
    platform: string,
    followers: number,
  ): Promise<{ platform: string; followers: number; snapshot_date: string; source: string }> {
    return apiClient.put(`analytics/brands/${brandId}/channels/${platform}/followers`, { followers });
  },

  async getUniversalOverview(range: AnalyticsRange): Promise<UniversalOverviewResponse> {
    return apiClient.get<UniversalOverviewResponse>('analytics/universal/overview', { range, tz: BROWSER_TZ });
  },

  async getUniversalEvents(query: EventsQuery = {}): Promise<EventsResponse> {
    return apiClient.get<EventsResponse>('analytics/universal/events', eventParams(query));
  },

  async getUserSummary(userId: string, range: AnalyticsRange = '30d'): Promise<UserSummaryResponse> {
    return apiClient.get<UserSummaryResponse>(`analytics/users/${userId}/summary`, { range, tz: BROWSER_TZ });
  },

  async getUserEvents(userId: string, query: EventsQuery = {}): Promise<EventsResponse> {
    return apiClient.get<EventsResponse>(`analytics/users/${userId}/events`, eventParams(query));
  },
};

export const {
  getBrandOverview,
  getBrandEvents,
  getUniversalOverview,
  getUniversalEvents,
  getUserSummary,
  getUserEvents,
} = analyticsApi;

export default analyticsApi;
